import { getPassiveEventOption, isClient } from '@mop/shared/utils/util';

type UseGlobalEvents = {
  fullScreenChangeRef: Ref<HTMLElement | null>;
  hasUserInteractedRef: Ref<boolean>;
};

type GlobalEventsStorage = {
  composable: UseGlobalEvents;
};

function getFullScreenElement(): HTMLElement | null {
  // @ts-ignore webkitFullscreenElement does not exist on HTMLElement
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

if (isClient && 'ontouchstart' in document.documentElement) {
  document.body.addEventListener(
    'touchstart',
    function touchStart() {
      // FIX because By default Safari on iOS does not use the :active state
      // unless there is a touchstart event handler on the relevant element or on the <body> element
    },
    getPassiveEventOption(),
  );
}

export default function useMopGlobalEventsClient(): UseGlobalEvents {
  const storage = initStorage<GlobalEventsStorage>('useGlobalEventsClient');
  const storageResult: UseGlobalEvents | null = storage.get('composable');
  if (storageResult !== null) {
    return storageResult;
  }

  // shallowRef: a DOM element assigned wholesale — never deep-proxy a DOM node.
  const fullScreenChangeRef: Ref<HTMLElement | null> = shallowRef(null);
  const hasUserInteractedRef: Ref<boolean> = ref(false);

  function handleFullScreenChange() {
    fullScreenChangeRef.value = getFullScreenElement();
  }

  // @ts-ignore
  let userInteractionTimer: NodeJS.Timeout = null;

  document.addEventListener('fullscreenchange', handleFullScreenChange, false);
  document.addEventListener('mozfullscreenchange', handleFullScreenChange, false);
  document.addEventListener('webkitfullscreenchange', handleFullScreenChange, false);

  document.addEventListener('mousemove', handleUserInteraction);
  document.addEventListener('touchstart', handleUserInteraction);
  document.addEventListener('click', handleUserInteraction);

  userInteractionTimer = setTimeout(() => {
    handleUserInteraction();
  }, 5500);

  function handleUserInteraction() {
    if (hasUserInteractedRef.value) {
      return;
    }
    clearTimeout(userInteractionTimer);
    document.removeEventListener('mousemove', handleUserInteraction);
    document.removeEventListener('touchstart', handleUserInteraction);
    document.removeEventListener('click', handleUserInteraction);
    hasUserInteractedRef.value = true;
  }

  return storage.saveAndGet('composable', {
    fullScreenChangeRef,
    hasUserInteractedRef,
  });
}
